import React from "react";
import { Stack, Box } from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";

import FormAlert from "../components/common/FormAlert";
import FormTextField from "../components/common/FormTextField";
import SubmitButton from "../components/buttons/SubmitButton";

import { useAppDispatch } from "../redux/store";
import {
  createPackageUnit,
  updatePackageUnit,
} from "../redux/actions/packageUnitActions";
import { cleanFormData } from "../helpers/formHelpers";
import { packageUnitActions } from "../redux/slices/packageUnit";

import { PackageUnitRequestDto } from "../shared/dtos/packageUnit/PackageUnitRequestDto";
import { PackageUnitResponseDto } from "../shared/dtos/packageUnit/PackageUnitResponseDto";

const packageUnitSchema = Yup.object().shape({
  name: Yup.string().required("Package name is required (e.g., Piece)"),
  code: Yup.string().required("Package code is required (e.g., P)"),
});

interface CreateOrUpdatePackageUnitFormProps {
  unit?: PackageUnitResponseDto | null;
  isEditing?: boolean;
  loading: boolean;
  error: string | null;
}

const CreateOrUpdatePackageUnitForm: React.FC<
  CreateOrUpdatePackageUnitFormProps
> = ({ unit = null, isEditing = false, loading = false, error = null }) => {
  const dispatch = useAppDispatch();

  const initialValues: Partial<PackageUnitRequestDto> = {
    name: unit?.name || "",
    code: unit?.code || "",
  };

  return (
    <Box sx={{ width: "100%" }}>
      <FormAlert
        message={error}
        severity="error"
        closable
        onClose={() =>
          dispatch(
            isEditing
              ? packageUnitActions.setErrorUpdate(null)
              : packageUnitActions.setErrorCreate(null),
          )
        }
      />
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={packageUnitSchema}
        onSubmit={(values) => {
          const payload = cleanFormData(
            values,
          ) as unknown as PackageUnitRequestDto;
          if (isEditing && unit) {
            dispatch(updatePackageUnit({ id: unit.id, values: payload }));
          } else {
            dispatch(createPackageUnit(payload));
          }
        }}
      >
        {({ dirty, isValid }: FormikProps<Partial<PackageUnitRequestDto>>) => (
          <Form>
            <Stack spacing={3}>
              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: { xs: "1fr", sm: "2fr 1fr" },
                }}
              >
                <FormTextField
                  name="name"
                  label="Package Name"
                  placeholder="e.g., Carton (12 pcs)"
                />
                <FormTextField
                  name="code"
                  label="Package Code"
                  placeholder="e.g., CTN-12"
                />
              </Box>

              <SubmitButton
                title={isEditing ? "Save Changes" : "Create Package Unit"}
                loading={loading}
                disabled={!dirty || !isValid || loading}
                startIcon={
                  isEditing ? <SaveOutlinedIcon /> : <AddCircleOutlineIcon />
                }
                fullWidth
                sx={{ py: 1.5, fontWeight: 700 }}
              />
            </Stack>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default CreateOrUpdatePackageUnitForm;
